import React, { useState } from 'react';
import axios from 'axios';
import { Link as LinkIcon, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

/**
 * NicheFinderAddChannelModal — wrapper proprio (NichosViraisModals.AddChannelModal
 * tem API legada com state externo). POST direto /nichos-virais/add-channel.
 */
const NicheFinderAddChannelModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('Outros/Não Classificado');
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    if (!url.trim()) {
      toast.error(t('viralNiches.enter_channel_url'));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/nichos-virais/add-channel`,
        { url: url.trim(), category },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data.success) {
        toast.success(data.message || t('viralNiches.processing'));
        onSuccess && onSuccess();
      } else {
        toast.error(data.message || t('viralNiches.error_add_channel'));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('viralNiches.error_add_channel'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="dp-modal-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
        animation: 'dp-modal-overlay 0.2s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--dp-bg-glass)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRadius: 16,
          padding: 28,
          maxWidth: 480,
          width: '100%',
          border: '1px solid var(--dp-border-medium)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)',
          fontFamily: 'var(--dp-font)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              color: 'var(--dp-text-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <LinkIcon size={20} />
            {t('viralNiches.add_channel_by_url')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: '1px solid var(--dp-border-subtle)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        <Field label={t('viralNiches.youtube_channel_url')}>
          <input
            className="dp-input"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder={t('viralNiches.url_placeholder')}
            disabled={submitting}
          />
          <p
            style={{
              margin: '6px 0 0 0',
              fontSize: 12,
              color: 'var(--dp-text-tertiary)',
            }}
          >
            {t('viralNiches.accepted_formats')}
          </p>
        </Field>

        <Field label={t('viralNiches.category')}>
          <input
            className="dp-input"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder={t('viralNiches.category_placeholder')}
            disabled={submitting}
          />
        </Field>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 8,
          }}
        >
          <button
            type="button"
            className="dp-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('viralNiches.cancel')}
          </button>
          <button
            type="button"
            className="dp-btn-primary"
            onClick={handleAdd}
            disabled={submitting || !url.trim()}
          >
            {submitting ? t('viralNiches.adding') : t('viralNiches.add_channel')}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label
      style={{
        display: 'block',
        fontSize: 11,
        fontWeight: 600,
        color: 'var(--dp-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 6,
      }}
    >
      {label}
    </label>
    {children}
  </div>
);

export default NicheFinderAddChannelModal;
